/* eslint-disable prefer-destructuring */
import styled, { css } from "styled-components";

import { type DisplayProps, type SpaceProps, display, space } from "styled-system";
import * as Flex from "../../globals/Flex";
import { ImageFluid } from "../../globals/Image";
import { theme } from "../../theme/toyotaTheme";
import * as BasketList from "./BasketList";
import * as MonthlyPrice from "../price/MonthlyPrice";
import * as CashPrice from "../price/CashPrice";
import * as Button from "../../components/Button";
import * as Type from "../../globals/Type";
import * as FinanceList from "../FinanceList";
import * as FinanceToggle from "../../components/FinanceToggle";
import { getBreakpoint } from "../../../../../themes/common";

export const BASKET_HEADER_TOP_MARGIN = theme.space[1];

export const Wrapper = styled.div<{ attached?: boolean; greyBackground?: boolean } & SpaceProps>`
    padding: ${theme.space[3]}px;
    background-color: ${({ greyBackground }) => (greyBackground ? theme.colors.grey1 : theme.colors.primaryWhite)};
    border-bottom: 0;
    ${space}
    ${CashPrice.Text} {
        color: ${theme.colors.primaryBlack};
    }
    ${({ attached }) =>
        attached &&
        css`
            border-top: none;
        `}

    @media ${getBreakpoint("up", "lg")} {
        background-color: ${theme.colors.primaryWhite};
    }
`;

export const Inner = styled.div<SpaceProps>`
    ${space}
`;

export const Title = styled.h2.attrs(() => ({
    className: "t-milli-headline",
}))`
    margin-top: ${theme.space[3]}px;
    padding-bottom: ${theme.space[2]}px;
    border-bottom: 1px solid ${theme.colors.grey2};
`;

export const TitlePaymentSwitch = styled.h2.attrs(() => ({
    className: "t-epsilon-text",
}))`
    margin-bottom: ${theme.space[1]}px;
    color: ${theme.colors.primaryBlack};
`;

export const DescriptionPaymentSwitch = styled.div.attrs(() => ({
    className: "t-base-text",
}))`
    margin-bottom: ${theme.space[1]}px;

    ${Button.Link},${Type.SmallText} {
        text-align: start;
        color: ${theme.colors.grey7};
    }
`;

export const CarTitle = styled.h2.attrs(() => ({
    className: "t-base-headline",
}))`
    margin: 0 0 4px;
    text-transform: none;
`;

export const CarDetails = styled.div`
    font-size: 1.3rem;
    line-height: 1.5;
    color: ${theme.colors.grey7};

    li {
        margin: 0;
        list-style: none;
    }
`;

export const FinanceQuotesWrapper = styled.div<DisplayProps>`
    ${display}

    &:not(:empty) {
        margin-top: ${theme.space[3]}px;
    }

    ${BasketList.BasketList} {
        padding: 0;
    }

    ${FinanceList.DescriptionTerm},
    ${FinanceList.DescriptionDetail} {
        border-bottom: 0;
    }

    ${BasketList.BasketListItem} {
        margin: 0;
        padding-top: ${theme.space[1]}px;
        padding-bottom: ${theme.space[1]}px;

        ${Flex.Col} {
            padding: 0;
        }
    }

    ${BasketList.ListItemLabel},
    ${BasketList.ListItemValue} {
        font-size: 1.3rem;
        line-height: 2.2rem;
        color: ${theme.colors.primaryBlack};
    }
`;
export const PaymentDetailsTitle = styled.h2`
    margin: 0 0 ${theme.space[1]}px;
    font-size: 1.3rem;
    line-height: 2rem;
    text-transform: uppercase;
`;

export const PaymentDetailsWrapper = styled.div`
    margin: ${theme.space[4]}px 0 ${theme.space[3]}px;

    dl {
        display: flex;
        flex-flow: row wrap;
    }

    dt {
        width: 40%;
        margin: 0;
        padding: ${theme.space[1]}px 0;
        font-size: 1.3rem !important;
        border-top: 1px solid ${theme.colors.grey2};
        @media ${getBreakpoint("up", "lg")} {
            margin-bottom: ${theme.space[2]}px;
            padding: 0;
        }

        &[data-rtl="true"] {
            flex-basis: 40%;
            margin-bottom: 0;
            text-align: right;
        }
    }
    dd {
        width: 60%;
        margin: 0;
        padding: ${theme.space[1]}px 0;
        font-family: ${theme.fonts.semiBold} !important;
        text-transform: uppercase;
        color: ${theme.colors.primaryBlack};
        border-top: 1px solid ${theme.colors.grey2};
        @media ${getBreakpoint("up", "lg")} {
            margin-bottom: ${theme.space[2]}px;
            padding: 0;
        }

        &[data-rtl="true"] {
            flex-basis: 60%;
            text-align: left;
        }
    }

    ${CashPrice.Text} {
        font-family: ${theme.fonts.semiBold};
        font-size: 1.3rem !important;
        text-transform: uppercase;
    }
`;

export const DisclaimerWrapper = styled.div`
    &:not(:empty) {
        padding-top: ${theme.space[3]}px;
    }

    font-size: 1.3rem;
    color: ${theme.colors.grey4};
`;

export const CtaWrapper = styled.div`
    position: relative;
    margin-bottom: ${theme.space[1]}px;
`;

export const CtaLink = styled.a`
    display: block;
    width: 100%;
    padding: 12px;
    text-align: center;
    background-color: ${theme.colors.primaryWhite};
    border: 1px solid ${theme.colors.grey2};
`;

export const CtaItem = styled.div<{ disabled?: boolean }>`
    display: flex;
    width: 50%;
    padding-bottom: ${theme.space[2]}px;
    padding-left: 15px;

    ${({ disabled }) =>
        disabled &&
        css`
            ${CtaLink} {
                cursor: default;
            }
        `}

    @media ${getBreakpoint("up", "lg")} {
        width: 25%;
    }

    html[dir="rtl"] & {
        padding-left: 0;
        padding-right: 15px;
    }
`;

export const CtaItems = styled.div<{ numItems?: number }>`
    display: flex;
    flex-wrap: wrap;
    margin: 0 0 0 -15px;
    /* Exception for when only two items */
    ${({ numItems }) =>
        numItems === 2 &&
        css`
            ${CtaItem} {
                @media ${getBreakpoint("up", "lg")} {
                    width: 50%;
                }
            }
        `};
    /* Exception for only three items */
    ${({ numItems }) =>
        numItems === 3 &&
        css`
            ${CtaItem} {
                @media ${getBreakpoint("up", "lg")} {
                    width: 33.3333%;
                }
            }
        `};
`;

export const CtaTitle = styled.span`
    display: block;
    font-size: 1.3rem;
    line-height: 2rem;
`;

export const BasketCtas = styled.div`
    display: none;
    @media ${getBreakpoint("up", "lg")} {
        display: block;
    }
`;

export const EtaWrapper = styled.div`
    margin: 0 -${theme.space[1]}px;
    padding: ${theme.space[2]}px ${theme.space[1]}px;
    background-color: ${theme.colors.grey1};
    @media ${getBreakpoint("up", "lg")} {
        margin: 0;
        padding: ${theme.space[2]}px;
    }
`;

export const EtaHeader = styled.div`
    display: flex;
    justify-content: flex-start;
    font-size: 1.3rem;
    line-height: 2rem;

    ${Button.Link} {
        .icon {
            position: relative;
            top: -2px;
        }
    }
`;

export const CarImage = styled(ImageFluid)`
    @media ${getBreakpoint("up", "lg")} {
        display: none;
    }
`;

export const BasketHeaderWrapper = styled.div<{ hideOnMobile?: boolean }>`
    margin-bottom: ${theme.space[2]}px;
    text-align: start;

    ${({ hideOnMobile }) =>
        hideOnMobile &&
        css`
            @media ${getBreakpoint("down", "lg")} {
                display: none;
            }
        `}
`;

export const ServicesWrapper = styled(Flex.Col)`
    ${CashPrice.Text},
    ${MonthlyPrice.Text} {
        font-size: 1.3rem;
        line-height: 2rem;
    }
`;

export const ServicePlanList = styled.div``;

export const ServicePlanListItem = styled.div`
    &:not(:last-child) {
        padding-bottom: ${theme.space[1]}px;
    }
    &:not(:first-child) {
        padding-top: ${theme.space[1]}px;
        border-top: 1px solid ${theme.colors.grey2};
    }
`;

export const ServicePlanListItemTerm = styled(Flex.Col).attrs(() => ({
    className: "t-milli-text",
}))`
    width: 60%;
    padding-right: 0;

    html[dir="rtl"] & {
        padding-left: 0;
    }
`;

export const ServicePlanListItemValue = styled(Flex.Col).attrs(() => ({
    className: "t-milli-headline",
}))`
    padding-left: 0;

    html[dir="rtl"] & {
        padding-right: 0;
    }
`;

export const BasketHeader = styled.div<DisplayProps>`
    margin-bottom: ${BASKET_HEADER_TOP_MARGIN}px;
    padding: ${theme.space[3]}px;
    background-color: ${theme.colors.primaryWhite};
    ${display};

    ${FinanceToggle.Wrapper} + dl {
        margin-top: ${theme.space[2]}px;
    }

    dl {
        display: flex;
        flex-wrap: wrap;
    }

    dt,
    dd {
        margin-bottom: 0; /* Override CSS: Remove bottom margin from dt/dd */
        padding: ${theme.space[1]}px 0;
        font-size: 1.3rem;
        line-height: 2rem;
        color: ${theme.colors.primaryBlack};
        border-bottom: 1px solid ${theme.colors.grey2};

        &:first-of-type {
            border-top: 1px solid ${theme.colors.grey2};
        }
    }

    dt {
        width: 60%;
        text-align: start;

        html[dir="rtl"] & {
            width: 100%;
        }
    }

    dd {
        flex-grow: 1;
        width: 40%;
        margin-left: 0 !important;
        font-family: ${theme.fonts.semiBold};
        text-align: end;
        &:first-of-type {
            text-transform: uppercase;
        }

        ${CashPrice.Text} {
            margin-top: 0;
            font-size: 1.3rem;
            line-height: 2rem;
            color: ${theme.colors.primaryBlack};
        }

        ${CashPrice.TextLineThrough} {
            color: ${theme.colors.primaryBlack};
        }
    }

    ${Button.CtaBasket} + ${Button.Primary} {
        margin-top: 0;
    }

    ${Button.Primary} {
        display: inline-block;
    }

    html[dir="rtl"] & {
        dd {
            width: 100%;
            margin-right: 0 !important;
            text-align: start;
            border-top: 0;
        }
    }
`;
